import { isZeroLiteral, numIsInteger, numScale, } from './ast.js';
import { FormulaErrorCodes, formulaError } from './errors.js';
import { acceptsArity, arityText, findFunction } from './registry.js';
import { lookupSymbol } from './resolver.js';
import { Types, describeResult, describeType, isCollection, isNumericResult, isTemporal, measureOf, resultType, sameType, scopeOf, } from './types.js';
import { addValueTypes, divideValueTypes, multiplyValueTypes, sameOrPlain } from './value-types.js';
import { warnUnguardedAll } from './warnings.js';
/**
 * Infers the type of every subexpression (§4.5): base type with Integer widening to Decimal, value type by the
 * value-type algebra, nullability, scale, and the unit or currency where the host declares one. Then checks the
 * root against the target (FL-E06, E07, E08). Runs on a resolved tree.
 *
 * An aggregate's expression and `where:` are inferred by a child checker over the collection's scope (§4.7); a
 * collection symbol anywhere else is FL-E03, since a collection is never a value.
 */
export function typeCheck(root, symbols, target, errors, nodeTypes = new Map()) {
    const checker = new TypeChecker(symbols, errors, nodeTypes);
    const result = checker.infer(root);
    if (result && target)
        checker.checkTarget(root, result, target);
    warnUnguardedAll(root, nodeTypes, errors);
    return result;
}
export class TypeChecker {
    constructor(symbols, errors, 
    /** The checker's per-node result, what the back-ends read (the C# twin's `NodeTypes`). */
    nodeTypes = new Map()) {
        this.symbols = symbols;
        this.errors = errors;
        this.nodeTypes = nodeTypes;
    }
    /** The type of `node`, or null after an error has been reported for it. */
    infer(node) {
        const result = this.inferNode(node);
        if (result)
            this.nodeTypes.set(node, result);
        return result;
    }
    /** A checker over a collection's scope, reporting into the same errors and node types: what an aggregate's body is inferred with. */
    child(scope) {
        return new TypeChecker(scope, this.errors, this.nodeTypes);
    }
    inferNode(node) {
        if (node.kind === 'num')
            return this.inferNumber(node);
        if (isLiteral(node))
            return inferLiteral(node);
        switch (node.kind) {
            case 'ref':
                return this.inferRef(node);
            case 'unary':
                return this.inferUnary(node);
            case 'binary':
                return this.inferBinary(node);
            case 'coalesce':
                return this.inferCoalesce(node);
            case 'cond':
                return this.inferCond(node);
            case 'call':
                return this.inferCall(node);
        }
    }
    /** A number literal within what System.Decimal holds exactly (§4.1); beyond it FL-E09, and nothing to evaluate. */
    inferNumber(node) {
        const excess = literalExcess(node);
        if (excess) {
            const [limit, actual] = excess;
            this.error(FormulaErrorCodes.LimitExceeded, node.span, `The number has ${actual} ${limit}; a number literal has at most ${MAX_LITERAL_DIGITS}.`, {
                limit,
                max: String(MAX_LITERAL_DIGITS),
                actual: String(actual),
            });
            return null;
        }
        return inferLiteral(node);
    }
    inferRef(node) {
        const symbol = lookupSymbol(node, this.symbols);
        if (!symbol)
            return null; // FL-E02 was reported by the resolver
        if (isCollection(symbol.type)) {
            this.baseTypeError(node.span, `'${symbol.name}' is a collection; only an aggregate such as sum() or count() can read it.`, { operand: 'Collection' });
            return null;
        }
        const scale = symbol.type.base === 'Integer' ? 0 : symbol.scale ?? null;
        return resultType(symbol.type, symbol.valueType, symbol.nullable, scale, symbol.unit ?? null, symbol.currency ?? null);
    }
    inferUnary(node) {
        const operand = this.infer(node.operand);
        if (!operand)
            return null;
        if (node.op === '!') {
            return this.requireBoolean(node, operand, "'!' needs a Boolean") ? booleanResult(false) : null;
        }
        if (!isNumericResult(operand)) {
            this.baseTypeError(node.span, `'-' needs a number; got ${describeResult(operand)}.`, { operand: describeResult(operand) });
            return null;
        }
        return operand;
    }
    inferBinary(node) {
        const left = this.infer(node.left);
        const right = this.infer(node.right);
        if (!left || !right)
            return null;
        if (ARITHMETIC_OPERATORS.has(node.op))
            return this.inferArithmetic(node, left, right);
        if (EQUALITY_OPERATORS.has(node.op))
            return this.inferEquality(node, left, right);
        if (RELATIONAL_OPERATORS.has(node.op))
            return this.inferRelational(node, left, right);
        return this.inferLogical(node, left, right);
    }
    inferArithmetic(node, left, right) {
        if (!isNumericResult(left) || !isNumericResult(right)) {
            this.baseTypeError(node.span, `'${node.op}' needs numbers; got ${describeResult(left)} and ${describeResult(right)}.`, {
                left: describeResult(left),
                right: describeResult(right),
            });
            return null;
        }
        const valueType = arithmeticValueType(node, left, right) ?? this.valueTypeFailure(node, left, right);
        this.reportMeasureMismatch(node.span, left, right);
        return arithmeticResult(node.op, valueType, left, right);
    }
    inferEquality(node, left, right) {
        if (this.unify(node, node.left, left, node.right, right) === undefined)
            return null;
        return this.checkComparableValueTypes(node, left, right) ? booleanResult(left.nullable || right.nullable) : null;
    }
    inferRelational(node, left, right) {
        const ordered = (isNumericResult(left) && isNumericResult(right)) ||
            (left.type !== null && right.type !== null && isTemporal(left.type) && sameType(left.type, right.type));
        if (!ordered) {
            this.baseTypeError(node.span, `'${node.op}' compares numbers or dates of one kind; got ${describeResult(left)} and ${describeResult(right)}.`, {
                left: describeResult(left),
                right: describeResult(right),
            });
            return null;
        }
        return this.checkComparableValueTypes(node, left, right) ? booleanResult(left.nullable || right.nullable) : null;
    }
    inferLogical(node, left, right) {
        if (left.type?.base !== 'Boolean' || right.type?.base !== 'Boolean') {
            this.baseTypeError(node.span, `'${node.op}' needs Booleans; got ${describeResult(left)} and ${describeResult(right)}.`, {
                left: describeResult(left),
                right: describeResult(right),
            });
            return null;
        }
        return booleanResult(false); // a null Boolean counts as false in a condition (§4.4)
    }
    inferCoalesce(node) {
        const left = this.infer(node.left);
        const right = this.infer(node.right);
        if (!left || !right)
            return null;
        return this.merge(node, node.left, left, node.right, right, right.nullable);
    }
    inferCond(node) {
        const test = this.infer(node.test);
        const then = this.infer(node.then);
        const otherwise = this.infer(node.else);
        if (!test || !then || !otherwise || !this.requireBoolean(node.test, test, "The condition of '?:' must be a Boolean"))
            return null;
        return this.merge(node, node.then, then, node.else, otherwise, then.nullable || otherwise.nullable);
    }
    inferCall(node) {
        const fn = findFunction(node.fn);
        if (!fn) {
            this.error(FormulaErrorCodes.UnknownSymbol, node.fnSpan, `'${node.fn}' is not a known function.`, { path: node.fn });
            return null;
        }
        if (!acceptsArity(fn, node)) {
            this.baseTypeError(node.span, `${node.fn}() takes ${arityText(fn)} argument(s); got ${node.args.length}.`, {
                function: node.fn,
                expected: arityText(fn),
                actual: String(node.args.length),
            });
            return null;
        }
        const undeclared = node.named.find((n) => !fn.namedParameters.has(n.name));
        if (undeclared) {
            this.baseTypeError(undeclared.value.span, `${node.fn}() has no named argument '${undeclared.name}'.`, {
                function: node.fn,
                name: undeclared.name,
            });
            return null;
        }
        const repeated = node.named.find((n, i) => node.named.findIndex((m) => m.name === n.name) !== i);
        if (repeated) {
            this.baseTypeError(repeated.value.span, `${node.fn}() names '${repeated.name}:' twice.`, { function: node.fn, name: repeated.name });
            return null;
        }
        const collection = fn.aggregate ? this.collectionArgument(node) : undefined;
        if (collection) {
            this.nodeTypes.set(node.args[0], resultType(Types.Collection, 'Plain', false, null));
            return fn.aggregate.infer({ call: node, collection, scope: this.child(scopeOf(collection)), checker: this });
        }
        const args = node.args.map((arg) => this.infer(arg));
        if (args.some((a) => a === null))
            return null;
        if (!fn.row) {
            this.baseTypeError(node.args[0].span, `${node.fn}() needs a collection as its first argument; got ${describeResult(args[0])}.`, {
                function: node.fn,
                operand: describeResult(args[0]),
            });
            return null;
        }
        return fn.row.infer({ call: node, args: args, checker: this });
    }
    /** The collection symbol a call's first argument names, when it is a reference to one. */
    collectionArgument(node) {
        const first = node.args[0];
        if (first?.kind !== 'ref')
            return undefined;
        const symbol = lookupSymbol(first, this.symbols);
        return symbol && isCollection(symbol.type) ? symbol : undefined;
    }
    /**
     * The type two branches or alternatives share (`??`, `?:`, `if()`): unified base type, the additive value-type
     * rule, one unit or currency, the larger scale.
     */
    merge(at, leftNode, left, rightNode, right, nullable) {
        const type = this.unify(at, leftNode, left, rightNode, right);
        if (type === undefined)
            return null;
        const valueType = addValueTypes(left.valueType, right.valueType, isZeroLiteral(leftNode), isZeroLiteral(rightNode)) ?? this.valueTypeFailure(at, left, right);
        this.reportMeasureMismatch(at.span, left, right);
        const [unit, currency] = carryMeasure(valueType, left, right);
        return resultType(type, valueType, nullable, maxScale(left, right), unit, currency);
    }
    /** Reports FL-E04 and yields the value type the failed subexpression carries from here on. */
    valueTypeFailure(at, left, right) {
        this.valueTypeError(at, left, right);
        return 'Unknown';
    }
    /** FL-E11 is reported and inference continues: the value type is right, only the unit or currency is not. */
    reportMeasureMismatch(span, left, right) {
        if (measuresDiffer(left, right))
            this.measureError(span, left, right);
    }
    /** The common base type of two operands (null for two null literals), or undefined with FL-E03 reported at `at`. */
    unify(at, leftNode, left, rightNode, right) {
        if (left.type === null || right.type === null)
            return left.type ?? right.type;
        if (isNumericResult(left) && isNumericResult(right))
            return widen(left.type, right.type);
        if (sameType(left.type, right.type) || this.adoptsCodes(left.type, right.type, rightNode))
            return left.type;
        if (this.adoptsCodes(right.type, left.type, leftNode))
            return right.type;
        this.baseTypeError(at.span, `Cannot compare or combine ${describeResult(left)} with ${describeResult(right)}.`, {
            left: describeResult(left),
            right: describeResult(right),
        });
        return undefined;
    }
    /**
     * An enum meets a String whose every value is one of its codes — a code literal, or a `?:`, `??` or `if()` whose
     * branches are (§4.5). The String then takes the enum's type, so a back-end that writes an enum's codes as its
     * members (the SQL templates) writes these as members too.
     */
    adoptsCodes(enumType, otherType, node) {
        if (enumType.base !== 'Enum' || otherType.base !== 'String' || !yieldsOnlyCodes(node, enumType.enumCodes ?? new Set()))
            return false;
        this.retype(node, enumType);
        return true;
    }
    /** Gives a code-yielding String and its code-yielding branches the enum's type, keeping the rest of what was inferred. */
    retype(node, enumType) {
        const inferred = this.nodeTypes.get(node);
        if (inferred)
            this.nodeTypes.set(node, { ...inferred, type: enumType });
        for (const branch of codeBranches(node))
            this.retype(branch, enumType);
    }
    /** A value-type mismatch in a comparison is reported; the comparison is still a Boolean. */
    checkComparableValueTypes(node, left, right) {
        if (!sameOrPlain(left.valueType, right.valueType))
            this.valueTypeError(node, left, right);
        return true;
    }
    requireBoolean(at, operand, what) {
        if (operand.type?.base === 'Boolean')
            return true;
        this.baseTypeError(at.span, `${what}; got ${describeResult(operand)}.`, { operand: describeResult(operand) });
        return false;
    }
    checkTarget(root, result, target) {
        const span = root.span;
        this.checkTargetType(root, result, target, span);
        if (result.nullable && !target.nullable) {
            this.error(FormulaErrorCodes.PossiblyNullResult, span, 'The result may be null, but the target is not nullable.');
        }
        this.checkTargetScale(result, target, span);
    }
    /** FL-E06: the base type must be assignable and, for a non-Plain target, the value type must be the target's. */
    checkTargetType(root, result, target, span) {
        if (result.type?.base === 'String' && target.type.base === 'Enum' && !yieldsOnlyCodes(root, target.type.enumCodes ?? new Set())) {
            this.error(FormulaErrorCodes.ResultTypeMismatch, span, `The result is a String, but the target is ${describeType(target.type)}; only its codes can be assigned to it.`, {
                expected: describeType(target.type),
                actual: 'String',
            });
            return;
        }
        if (!isAssignable(result.type, target.type)) {
            this.error(FormulaErrorCodes.ResultTypeMismatch, span, `The result is ${describeResult(result)}, but the target is ${describeType(target.type)}.`, {
                expected: describeType(target.type),
                actual: describeResult(result),
            });
        }
        else if (target.valueType !== 'Plain' && result.valueType !== target.valueType && result.valueType !== 'Unknown') {
            this.error(FormulaErrorCodes.ResultTypeMismatch, span, `The result is ${result.valueType}, but the target is ${target.valueType}.`, {
                expected: target.valueType,
                actual: result.valueType,
            });
        }
    }
    /** FL-E08: a result whose scale is unbounded or larger than the target's must be wrapped in round(). */
    checkTargetScale(result, target, span) {
        const targetScale = target.scale ?? null;
        if (targetScale === null || (result.scale !== null && result.scale <= targetScale))
            return;
        const scale = result.scale === null ? 'unbounded' : String(result.scale);
        this.error(FormulaErrorCodes.ScaleExceedsTarget, span, `The result can have ${scale} decimals, but the target has ${targetScale}; wrap it in round().`, {
            scale,
            target: String(targetScale),
        });
    }
    valueTypeError(at, left, right) {
        const op = at.kind === 'binary' ? at.op : at.kind;
        this.error(FormulaErrorCodes.ValueTypeMismatch, at.span, `'${op}' cannot combine ${left.valueType} and ${right.valueType}.`, {
            left: left.valueType,
            right: right.valueType,
        });
    }
    measureError(span, left, right) {
        const what = left.valueType === 'Quantity' ? 'unit' : 'currency';
        this.error(FormulaErrorCodes.UnitOrCurrencyMismatch, span, `Both sides must share one ${what}; got ${measureOf(left)} and ${measureOf(right)}.`, {
            left: measureOf(left),
            right: measureOf(right),
        });
    }
    baseTypeError(span, message, params) {
        this.error(FormulaErrorCodes.BaseTypeMismatch, span, message, params);
    }
    error(code, span, message, params = {}) {
        this.errors.push(formulaError(code, span, message, params));
    }
}
const ARITHMETIC_OPERATORS = new Set(['+', '-', '*', '/']);
const EQUALITY_OPERATORS = new Set(['==', '!=']);
const RELATIONAL_OPERATORS = new Set(['<', '<=', '>', '>=']);
const LITERAL_KINDS = new Set(['num', 'str', 'bool', 'null']);
function isLiteral(node) {
    return LITERAL_KINDS.has(node.kind);
}
function inferLiteral(node) {
    switch (node.kind) {
        case 'num':
            return resultType(numIsInteger(node) ? Types.Integer : Types.Decimal, 'Plain', false, numScale(node));
        case 'str':
            return resultType(Types.String, 'Plain', false, null);
        case 'bool':
            return resultType(Types.Boolean, 'Plain', false, null);
        default:
            return resultType(null, 'Plain', true, null);
    }
}
/** Base type (Integer stays Integer except under division), nullability (a division is always possibly null), scale and measure of an arithmetic result. */
function arithmeticResult(op, valueType, left, right) {
    const isDivision = op === '/';
    const type = isDivision ? Types.Decimal : widen(left.type, right.type);
    const scale = op === '+' || op === '-' ? maxScale(left, right) : op === '*' ? sumScale(left, right) : null;
    const [unit, currency] = carryMeasure(valueType, left, right);
    return resultType(type, valueType, left.nullable || right.nullable || isDivision, scale, unit, currency);
}
function arithmeticValueType(node, left, right) {
    switch (node.op) {
        case '+':
        case '-':
            return addValueTypes(left.valueType, right.valueType, isZeroLiteral(node.left), isZeroLiteral(node.right));
        case '*':
            return multiplyValueTypes(left.valueType, right.valueType);
        default:
            return divideValueTypes(left.valueType, right.valueType);
    }
}
function isAssignable(result, target) {
    return (result === null ||
        sameType(result, target) ||
        (result.base === 'Integer' && target.base === 'Decimal') ||
        (result.base === 'String' && target.base === 'Enum') // only when every value position is a code; checked first
    );
}
/**
 * A String expression is assignable to and comparable with an enum when every value it can yield is one of the
 * enum's codes (§4.5): a code literal, null, or a `?:`, `??` or `if()` whose branches are. A reference or any other
 * String is not.
 */
function yieldsOnlyCodes(node, codes) {
    if (node.kind === 'str')
        return codes.has(node.value);
    if (node.kind === 'null')
        return true;
    const branches = codeBranches(node);
    return branches.length > 0 && branches.every((branch) => yieldsOnlyCodes(branch, codes));
}
/** The positions of a String expression whose values it yields: the branches of `?:`, `??` and `if()`. */
function codeBranches(node) {
    switch (node.kind) {
        case 'cond':
            return [node.then, node.else];
        case 'coalesce':
            return [node.left, node.right];
        case 'call':
            return node.fn === 'if' && node.args.length === 3 ? [node.args[1], node.args[2]] : [];
        default:
            return [];
    }
}
/** What System.Decimal holds exactly and the language's precision (§4.4): at most 28 digits and 28 decimals. */
const MAX_LITERAL_DIGITS = 28;
/** The limit a number literal exceeds and by how much: its digits (leading zeros aside), then its decimals; null within both. */
function literalExcess(node) {
    const digits = node.value.replace('.', '').replace(/^0+(?=\d)/, '').length;
    if (digits > MAX_LITERAL_DIGITS)
        return ['digits', digits];
    const decimals = numScale(node);
    return decimals > MAX_LITERAL_DIGITS ? ['decimals', decimals] : null;
}
function widen(left, right) {
    return left.base === 'Integer' && right.base === 'Integer' ? Types.Integer : Types.Decimal;
}
function maxScale(left, right) {
    return left.scale !== null && right.scale !== null ? Math.max(left.scale, right.scale) : null;
}
function sumScale(left, right) {
    return left.scale !== null && right.scale !== null ? left.scale + right.scale : null;
}
/** FL-E11 applies where both sides carry the same value type and each knows its unit or currency. */
function measuresDiffer(left, right) {
    const l = measureOf(left);
    const r = measureOf(right);
    return left.valueType === right.valueType && l !== null && r !== null && l !== r;
}
/** The unit or currency of the result: the operand of the result's value type, or the derivation's source. */
function carryMeasure(result, left, right) {
    if (left.valueType === result)
        return [left.unit, left.currency];
    if (right.valueType === result)
        return [right.unit, right.currency];
    switch (result) {
        case 'Amount':
            return [null, left.valueType === 'Rate' ? left.currency : right.currency];
        case 'Rate':
            return [right.unit, left.currency];
        default:
            return [null, null];
    }
}
function booleanResult(nullable) {
    return resultType(Types.Boolean, 'Plain', nullable, null);
}
